import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Database, RefreshCw, Plus, ListChecks, ArrowUpRight } from "lucide-react";
import MongoServerCard from "@/components/mongo/servers/MongoServerCard";
import SpokeConnectionCard from "@/components/mongo/servers/SpokeConnectionCard";
import AddServerForm from "@/components/mongo/servers/AddServerForm";

const BTN = "px-2.5 py-1.5 text-xs border border-white/10 rounded-md hover:bg-white/5 inline-flex items-center gap-1.5 transition-colors disabled:opacity-40";

export default function MongoServersPanel() {
    const [data, setData] = useState(null);
    const [adding, setAdding] = useState(false);
    const [testingAll, setTestingAll] = useState(false);
    const load = useCallback(async () => {
        try { setData((await api.get("/admin/mongo-mcp/servers")).data); }
        catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load MongoDB servers"); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const testAll = async () => {
        setTestingAll(true);
        const ready = data.items.filter((r) => r.url && r.token_set);
        const results = await Promise.all(ready.map((r) => api.post(`/admin/mongo-mcp/servers/${r.id}/test`).then((res) => res.data.ok)
            .catch((e) => { toast.error(`${r.label}: ${formatApiErrorDetail(e?.response?.data?.detail)}`); return false; })));
        const passed = results.filter(Boolean).length;
        setTestingAll(false);
        toast[passed === ready.length ? "success" : "error"](`${passed} of ${ready.length} connections passed the full check`);
        load();
    };

    if (!data) return <div className="text-xs text-white/40 font-mono" data-testid="mongo-servers-loading">Loading MongoDB servers…</div>;
    if (data.role !== "hub") return <SpokeConnectionCard data={data} />;
    const linkedBy = Object.fromEntries(data.items.filter((r) => r.env_id).map((r) => [r.env_id, r.label]));
    return (
        <section className="rounded-lg border border-white/10 bg-gradient-to-br from-white/[0.025] to-transparent p-4" data-testid="mongo-servers-panel">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-2.5">
                    <Database className="w-4 h-4 text-emerald-300 mt-0.5" strokeWidth={1.5} />
                    <div>
                        <h2 className="text-sm font-medium">MongoDB servers</h2>
                        <p className="text-[11px] text-white/45 mt-0.5 max-w-2xl leading-relaxed">
                            One MongoDB MCP connection per Radar environment. Each server receives only its own connection from this hub;
                            until it arrives, MongoDB tools stay off there. Test runs from this hub straight to the server.
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Link to="/settings/mongodb-mcp" data-testid="mongo-servers-field-map-link" className={`${BTN} border-violet-300/30 text-violet-200`}>
                        Field map v{data.field_map?.version || 0}{data.pending_changes ? ` · ${data.pending_changes} unpublished` : ""} <ArrowUpRight className="w-3 h-3" />
                    </Link>
                    <button onClick={testAll} disabled={testingAll} className={BTN} data-testid="mongo-servers-test-all">
                        <ListChecks className="w-3 h-3" /> {testingAll ? "Testing…" : "Test all"}
                    </button>
                    <button onClick={() => setAdding((v) => !v)} className={BTN} data-testid="mongo-servers-add-open"><Plus className="w-3 h-3" /> Add market</button>
                    <button onClick={load} className={BTN} data-testid="mongo-servers-refresh"><RefreshCw className="w-3 h-3" /></button>
                </div>
            </div>
            {adding && <AddServerForm onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />}
            <div className="space-y-2.5" data-testid="mongo-servers-list">
                {data.items.map((r) => (
                    <MongoServerCard key={r.id} row={r} environments={data.environments} linkedBy={linkedBy} onChanged={load} />
                ))}
            </div>
        </section>
    );
}
